/**
 * Currency Format Hook
 * Provides currency formatting based on company settings
 */

import { useQuery } from '@tanstack/react-query';
import config from '@/lib/config';

// Currency format configurations
export const CURRENCY_FORMATS = {
  DE: {
    code: 'DE',
    label: 'Deutsch (1.234,56 €)',
    thousandsSeparator: '.',
    decimalSeparator: ',',
    currencySymbol: '€',
    currencyPosition: 'after', // before or after
    spaceBetween: true,
  },
  US: {
    code: 'US',
    label: 'US ($ 1,234.56)',
    thousandsSeparator: ',',
    decimalSeparator: '.',
    currencySymbol: '$',
    currencyPosition: 'before',
    spaceBetween: true,
  },
  UK: {
    code: 'UK',
    label: 'UK (£ 1,234.56)',
    thousandsSeparator: ',',
    decimalSeparator: '.',
    currencySymbol: '£',
    currencyPosition: 'before',
    spaceBetween: true,
  },
  CH: {
    code: 'CH',
    label: 'Schweiz (CHF 1\'234.56)',
    thousandsSeparator: '\'',
    decimalSeparator: '.',
    currencySymbol: 'CHF',
    currencyPosition: 'before',
    spaceBetween: true,
  },
  FR: {
    code: 'FR',
    label: 'Französisch (1 234,56 €)',
    thousandsSeparator: ' ',
    decimalSeparator: ',',
    currencySymbol: '€',
    currencyPosition: 'after',
    spaceBetween: true,
  },
  AT: {
    code: 'AT',
    label: 'Österreich (€ 1.234,56)',
    thousandsSeparator: '.',
    decimalSeparator: ',',
    currencySymbol: '€',
    currencyPosition: 'before',
    spaceBetween: true,
  },
} as const;

export type CurrencyFormatCode = keyof typeof CURRENCY_FORMATS;

const DEFAULT_FORMAT = CURRENCY_FORMATS.DE;

interface CompanyDetails {
  currencyFormat?: string;
}

/**
 * Fetch company details from API
 */
const fetchCompanyDetails = async (): Promise<CompanyDetails> => {
  const token = localStorage.getItem('auth-data');
  const response = await fetch(`${config.apiUrl}/api/company-details`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch company details');
  }

  return response.json();
};

/**
 * Format a number as currency according to the selected format
 */
export function formatCurrency(
  amount: number | string | null | undefined,
  formatCode: CurrencyFormatCode = 'DE'
): string {
  // Handle null, undefined, or empty string
  if (amount === null || amount === undefined || amount === '') {
    return formatCurrencyWithConfig(0, CURRENCY_FORMATS[formatCode] || DEFAULT_FORMAT);
  }

  // Convert string to number
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Handle NaN
  if (isNaN(numericAmount)) {
    return formatCurrencyWithConfig(0, CURRENCY_FORMATS[formatCode] || DEFAULT_FORMAT);
  }

  const format = CURRENCY_FORMATS[formatCode] || DEFAULT_FORMAT;
  return formatCurrencyWithConfig(numericAmount, format);
}

/**
 * Internal function to format currency with a specific configuration
 */
function formatCurrencyWithConfig(
  amount: number,
  format: typeof CURRENCY_FORMATS[CurrencyFormatCode]
): string {
  // Split into integer and decimal parts
  const isNegative = amount < 0;
  const absoluteAmount = Math.abs(amount);
  const parts = absoluteAmount.toFixed(2).split('.');
  const integerPart = parts[0] || '0';
  const decimalPart = parts[1] || '00';

  // Add thousands separators
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousandsSeparator);

  // Combine with decimal separator
  const formattedAmount = `${formattedInteger}${format.decimalSeparator}${decimalPart}`;

  // Add currency symbol
  const space = format.spaceBetween ? ' ' : '';
  let result: string;
  
  if (format.currencyPosition === 'before') {
    result = `${format.currencySymbol}${space}${formattedAmount}`;
  } else {
    result = `${formattedAmount}${space}${format.currencySymbol}`;
  }

  // Add negative sign if needed
  return isNegative ? `-${result}` : result;
}

/**
 * Hook to get currency formatting function based on company settings
 */
export function useCurrencyFormat() {
  const { data: companyDetails } = useQuery<CompanyDetails>({
    queryKey: ['company-details'],
    queryFn: fetchCompanyDetails,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const formatCode = (companyDetails?.currencyFormat as CurrencyFormatCode) || 'DE';

  return {
    formatCurrency: (amount: number | string | null | undefined) => formatCurrency(amount, formatCode),
    currencyFormat: CURRENCY_FORMATS[formatCode] || DEFAULT_FORMAT,
    formatCode,
  };
}
